import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, doc, updateDoc, deleteDoc, setDoc, getDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';
import type { Bus, Student } from '../App';
import { useTranslation } from 'react-i18next';

interface BusManagementProps {
  schoolId: string; 
  mode: 'form' | 'list';
  schoolName?: string;
  schoolTechnicalId?: string;
}

const BusManagement: React.FC<BusManagementProps> = ({ 
  schoolId, 
  mode: initialMode,
  schoolName,
  schoolTechnicalId 
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const csvInputRef = useRef<HTMLInputElement>(null);

  const [buses, setBuses] = useState<Bus[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [viewMode, setViewMode] = useState<'form' | 'list'>(initialMode);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedBusStudents, setSelectedBusStudents] = useState<{busNum: string, list: Student[]} | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [schoolCoords, setSchoolCoords] = useState<{lat: number, lng: number}>({ lat: 33.58831, lng: -7.61138 });
  const [internalSchoolId, setInternalSchoolId] = useState<string>('');

  const [sortBy, setSortBy] = useState<'number' | 'status'>('number');
  const [filterStatus, setFilterStatus] = useState<'all' | 'Actif' | 'Inactif'>('all');

  const initialFormState: Partial<Bus> = {
    number: '',
    driverName: '',
    driverPhone: '',
    driver2Name: '',   // ✅ NOUVEAU
    driver2Phone: '',  // ✅ NOUVEAU
    password: '',
    capacity: 0,
    licensePlate: '',
    status: 'Actif',
    routeDescription: '', 
    isTracking: false
  };

  const [formData, setFormData] = useState<Partial<Bus>>(initialFormState);

  const toggleBusStatus = async (busId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'Actif' ? 'Inactif' : 'Actif';
      await updateDoc(doc(db, "schools", schoolId, "buses", busId), { status: newStatus });
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    setViewMode(initialMode);
    if (initialMode === 'form') {
      setEditingId(null);
      setFormData(initialFormState);
    }
  }, [initialMode]);

  useEffect(() => {
    if (!schoolId) return;

    const fetchSchoolData = async () => {
        const sSnap = await getDoc(doc(db, "schools", schoolId));
        if (sSnap.exists()) {
            const data = sSnap.data();
            
            if (data.schoolId) {
                setInternalSchoolId(data.schoolId); 
            } else if (data.schoolTechnicalId) {
                setInternalSchoolId(data.schoolTechnicalId);
            }

            if (data.latitude && data.longitude) {
                setSchoolCoords({ lat: Number(data.latitude), lng: Number(data.longitude) });
            }
        }
    };
    fetchSchoolData();

    const unsubBuses = onSnapshot(collection(db, "schools", schoolId, "buses"), (snap) => {
      setBuses(snap.docs.map(d => ({ id: d.id, ...d.data() } as Bus)));
    });

    const unsubStudents = onSnapshot(collection(db, "schools", schoolId, "students"), (snap) => {
      setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() } as Student)));
    });

    return () => { unsubBuses(); unsubStudents(); };
  }, [schoolId]);

  const filteredAndSortedBuses = useMemo(() => {
    let result = [...buses];
    
    if (filterStatus !== 'all') {
      result = result.filter(bus => bus.status === filterStatus);
    }
    
    result = result.filter(bus => 
      bus.number.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      bus.driverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bus.routeDescription && bus.routeDescription.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    if (sortBy === 'number') {
      result.sort((a, b) => {
        const numA = parseInt(String(a.number)) || 0;
        const numB = parseInt(String(b.number)) || 0;
        return numA - numB;
      });
    } else {
      result.sort((a, b) => {
        if (a.status === 'Actif' && b.status !== 'Actif') return -1;
        if (a.status !== 'Actif' && b.status === 'Actif') return 1;
        return 0;
      });
    }
    
    return result;
  }, [buses, searchTerm, filterStatus, sortBy]);

  const handleSave = async () => {
    if (!formData.number || !formData.driverName || !formData.password) {
        return alert(t('err_required_fields'));
    }

    try {
      const busNumValue = String(formData.number).trim();

      if (!editingId) {
        const busesRef = collection(db, "schools", schoolId, "buses");
        const q = query(busesRef, where('number', '==', busNumValue));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          alert(t('errorBusAlreadyExists', "Ce numéro de bus existe déjà"));
          return;
        }
      }

      const busData = { 
        ...formData, 
        number: busNumValue,
        driverName: formData.driverName?.trim(),
        driver2Name: formData.driver2Name?.trim() || '',
        driver2Phone: formData.driver2Phone?.trim() || '',
        password: formData.password?.trim(),
        capacity: Number(formData.capacity) || 0,
        routeDescription: formData.routeDescription?.trim() || '',
        licensePlate: formData.licensePlate?.trim() || '',
        status: formData.status || 'Actif',
        schoolId: internalSchoolId || schoolId, 
        updatedAt: new Date()
      };

      if (editingId) {
        await updateDoc(doc(db, "schools", schoolId, "buses", editingId), busData);
      } else {
        const newBusRef = doc(collection(db, "schools", schoolId, "buses"));
        await setDoc(newBusRef, {
          ...busData,
          id: newBusRef.id,
          latitude: schoolCoords.lat,
          longitude: schoolCoords.lng,
          isTracking: false,
          createdAt: new Date()
        });
      }
      
      setFormData(initialFormState);
      setEditingId(null);
      setViewMode('list');
    } catch (e) { 
      console.error(e);
      alert(t('alert_error')); 
    }
  };

  const handleDelete = async (id: string | undefined, number: string) => {
    if (id && window.confirm(`${t('confirm_delete_bus')} ${number} ?`)) {
      await deleteDoc(doc(db, "schools", schoolId, "buses", id));
    }
  };

  const handleBusCSVImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      if (!text) return alert("Erreur de lecture du fichier.");
      const lines = text.split('\n').filter(line => line.trim() !== '');
      if (lines.length <= 1) return alert("Le fichier est vide ou ne contient que l'en-tête.");
      
      const separator = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].toLowerCase().split(separator).map(h => h.trim());
      
      const busNumberIndex = headers.findIndex(h => h.includes('bus') || h.includes('numero'));
      const driverNameIndex = headers.findIndex(h => h.includes('driver') || h.includes('nom') || h.includes('conducteur'));
      const passwordIndex = headers.findIndex(h => h.includes('password') || h.includes('mot de passe'));
      const routeIndex = headers.findIndex(h => h.includes('route') || h.includes('description') || h.includes('trajet'));
      const statusIndex = headers.findIndex(h => h.includes('status') || h.includes('statut'));
      
      const batch = writeBatch(db);
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const columns = lines[i].split(separator).map(v => v?.trim());
        
        const busNumber = busNumberIndex !== -1 ? columns[busNumberIndex] : columns[0];
        const driverName = driverNameIndex !== -1 ? columns[driverNameIndex] : columns[1];
        const password = passwordIndex !== -1 ? columns[passwordIndex] : columns[2];
        const routeDescription = routeIndex !== -1 ? columns[routeIndex] : (columns[3] || '');
        const status = statusIndex !== -1 ? columns[statusIndex] : 'Actif';
        
        if (!busNumber || !driverName || !password) {
          errorCount++;
          continue;
        }
        
        const busesRef = collection(db, "schools", schoolId, "buses");
        const q = query(busesRef, where('number', '==', busNumber));
        const existingBus = await getDocs(q);
        
        if (!existingBus.empty) {
          errorCount++;
          continue;
        }
        
        const newBusRef = doc(collection(db, "schools", schoolId, "buses"));
        batch.set(newBusRef, {
          number: busNumber,
          driverName: driverName,
          password: password,
          routeDescription: routeDescription,
          status: status === 'Actif' || status === 'active' || status === 'true' ? 'Actif' : 'Inactif',
          schoolId: internalSchoolId || schoolId,
          latitude: schoolCoords.lat,
          longitude: schoolCoords.lng,
          isTracking: false,
          createdAt: new Date(),
          id: newBusRef.id
        });
        successCount++;
      }
      
      try { 
        await batch.commit(); 
        alert(`${successCount} bus importés avec succès !${errorCount > 0 ? ` ${errorCount} bus ignorés (existants ou erreurs).` : ''}`);
      } catch (e) { 
        console.error(e);
        alert("Erreur lors de l'import."); 
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  return (
    <div className="student-management-wrapper" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {selectedBusStudents && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '20px', maxWidth: '550px', width: '100%', maxHeight: '80vh', overflowY: 'auto', textAlign: isRTL ? 'right' : 'left' }}>
            <h2 style={{ margin: '0 0 10px 0', color: '#1a202c' }}>📋 {t('list_title')}: Bus {selectedBusStudents.busNum}</h2>
            <div style={{ background: '#ebf8ff', color: '#2b6cb0', padding: '10px 15px', borderRadius: '10px', fontWeight: 'bold', marginBottom: '20px' }}>
                {selectedBusStudents.list.length} {t('students_assigned')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedBusStudents.list.map(s => (
                <div key={s.id} style={{ padding: '15px', background: '#f7fafc', borderRadius: '12px', borderLeft: isRTL ? 'none' : '5px solid #3182ce', borderRight: isRTL ? '5px solid #3182ce' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ fontWeight: 'bold' }}>{s.name}</div>
                    <div style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 8px', borderRadius: '10px' }}>{s.studentClass}</div>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '5px' }}>📍 {s.address || t('err_no_address')}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setSelectedBusStudents(null)} style={{ marginTop: '25px', width: '100%', padding: '15px', borderRadius: '12px', border: 'none', background: '#2d3748', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>{t('btn_close').toUpperCase()}</button>
          </div>
        </div>
      )}

      {viewMode === 'list' ? (
        <div className="student-form-container" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 900 }}>{t('fleet_title').toUpperCase()} ({buses.length})</h1>
            
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e0', background: '#f1f5f9', fontWeight: 'bold', cursor: 'pointer' }}>
                <option value="all">{t('filter_all_buses')}</option>
                <option value="Actif">{t('filter_active')}</option>
                <option value="Inactif">{t('filter_inactive')}</option>
              </select>
              
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e0', background: '#f1f5f9', fontWeight: 'bold', cursor: 'pointer' }}>
                <option value="number">{t('sort_by_number')}</option>
                <option value="status">{t('sort_by_status')}</option>
              </select>
              
              <input type="text" placeholder={t('p_search')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e0', fontSize: '0.9rem', width: '200px', textAlign: isRTL ? 'right' : 'left' }} />
              
              <input type="file" ref={csvInputRef} onChange={handleBusCSVImport} accept=".csv" style={{ display: 'none' }} />
              <button onClick={() => csvInputRef.current?.click()} style={{ padding: '8px 15px', background: '#4a5568', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>📥 Import CSV</button>
              <button onClick={() => { setEditingId(null); setFormData(initialFormState); setViewMode('form'); }} style={{ padding: '8px 15px', background: '#3182ce', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>+ {t('bus_label').toUpperCase()}</button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: isRTL ? 'right' : 'left' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f0f2f5', color: '#718096', fontSize: '0.85rem' }}>
                  <th style={{ padding: '12px' }}>{t('bus_label').toUpperCase()}</th>
                  <th style={{ padding: '12px' }}>{t('th_driver').toUpperCase()}</th>
                  <th style={{ padding: '12px' }}>{t('map_legend_students').toUpperCase()}</th>
                  <th style={{ padding: '12px' }}>{t('th_access').toUpperCase()}</th>
                  <th style={{ padding: '12px' }}>{t('th_status').toUpperCase()}</th>
                  <th style={{ padding: '12px' }}>{t('th_actions').toUpperCase()}</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedBuses.map(bus => {
                  const busStudents = students.filter(s => String(s.assignedBusNumber) === String(bus.number));
                  return (
                    <tr key={bus.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                      <td style={{ padding: '12px' }}>
                        <strong>{t('bus_label')} {bus.number}</strong>
                        {bus.routeDescription && (
                          <div style={{ fontSize: '0.75rem', color: '#3182ce', marginTop: '2px' }}>
                            {bus.routeDescription}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: 'bold' }}>{bus.driverName}</div>
                        {(bus as any).driver2Name && (
                          <div style={{ fontSize: '0.75rem', color: '#718096', fontStyle: 'italic', marginTop: '2px' }}>
                            + {(bus as any).driver2Name}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button onClick={() => setSelectedBusStudents({ busNum: bus.number, list: busStudents })} style={{ background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                          {t('btn_list')} ({busStudents.length})
                        </button>
                      </td>
                      <td style={{ padding: '12px' }}><code style={{ background: '#edf2f7', padding: '4px 8px' }}>{bus.password}</code></td>
                      <td style={{ padding: '12px' }}>
                        <span 
                          onClick={() => toggleBusStatus(bus.id!, bus.status || 'Inactif')}
                          style={{ 
                            fontSize: '0.65rem', padding: '3px 8px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer',
                            background: bus.status === 'Actif' ? '#c6f6d5' : '#fed7d7', 
                            color: bus.status === 'Actif' ? '#2f855a' : '#c53030' 
                          }}
                        >
                          {bus.status === 'Actif' ? t('status_active').toUpperCase() : t('status_inactive').toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '12px', display: 'flex', gap: '8px' }}>
                        <button onClick={() => { setFormData(bus); setEditingId(bus.id); setViewMode('form'); }} style={{ background: '#fef3c7', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}>✏️</button>
                        <button onClick={() => handleDelete(bus.id, bus.number)} style={{ background: '#fee2e2', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}>🗑️</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="student-form-container">
          <div style={{ marginBottom: '30px', textAlign: 'center' }}>
            <h1 style={{ margin: 0, fontSize: '1.7rem', fontWeight: 900, color: '#1a202c' }}>
              {editingId ? t('title_edit_bus', "MISE À JOUR DES INFORMATIONS DU BUS") : t('btn_add_bus').toUpperCase()}
            </h1>
          </div>
          
          <div className="form-grid-main" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="form-column">
              <div className="section-title">🚌 {t('section_vehicle')}</div>
              <div className="input-with-icon">
                <span className="input-icon-field">🆔</span>
                <input className="input-styled-icon" placeholder={t('bus_label') + " №"} value={formData.number || ''} onChange={e => setFormData({...formData, number: e.target.value})} />
              </div>
              <div className="input-with-icon">
                <span className="input-icon-field">📋</span>
                <input className="input-styled-icon" placeholder={t('th_plate')} value={formData.licensePlate || ''} onChange={e => setFormData({...formData, licensePlate: e.target.value})} />
              </div>
              <div className="input-with-icon">
                <span className="input-icon-field">⚙️</span>
                <select 
                  className="input-styled-icon" 
                  value={formData.status || 'Actif'} 
                  onChange={e => setFormData({...formData, status: e.target.value})}
                  style={{ width: '100%', background: 'white' }}
                >
                  <option value="Actif">✅ {t('status_active') || 'Actif'}</option>
                  <option value="Inactif">❌ {t('status_inactive') || 'Inactif'}</option>
                </select>
              </div>
            </div>

            <div className="form-column">
              <div className="section-title">👨‍✈️ {t('section_driver')}</div>
              <div className="input-with-icon">
                <span className="input-icon-field">👤</span>
                <input className="input-styled-icon" placeholder={t('th_driver')} value={formData.driverName || ''} onChange={e => setFormData({...formData, driverName: e.target.value})} />
              </div>
              <div className="input-with-icon">
                <span className="input-icon-field">📞</span>
                <input className="input-styled-icon" placeholder={t('p_driver_phone')} value={formData.driverPhone || ''} onChange={e => setFormData({...formData, driverPhone: e.target.value})} />
              </div>
              <div className="input-with-icon">
                <span className="input-icon-field">🔑</span>
                <input className="input-styled-icon" placeholder={t('th_password')} value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} />
              </div>
            </div>
          </div>

          {/* ✅ SECTION SECOND CHAUFFEUR (PLEINE LARGEUR) */}
          <div style={{ marginTop: '20px', width: '100%' }}>
            <div className="section-title">👨‍✈️ {t('section_driver2', 'Second Chauffeur')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="input-with-icon">
                <span className="input-icon-field">👤</span>
                <input 
                  className="input-styled-icon" 
                  placeholder={t('th_driver2', 'Nom du 2ème chauffeur')} 
                  value={formData.driver2Name || ''} 
                  onChange={e => setFormData({...formData, driver2Name: e.target.value})} 
                />
              </div>
              <div className="input-with-icon">
                <span className="input-icon-field">📞</span>
                <input 
                  className="input-styled-icon" 
                  placeholder={t('p_driver2_phone', 'Téléphone du 2ème chauffeur')} 
                  value={formData.driver2Phone || ''} 
                  onChange={e => setFormData({...formData, driver2Phone: e.target.value})} 
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '25px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="section-title" style={{ textAlign: 'center', marginBottom: '8px', fontSize: isRTL ? '1.1rem' : '0.9rem', fontWeight: 800 }}>
              Pathway 🛣️ {t('route_description')}
            </div>
            <div className="input-with-icon" style={{ width: '100%', maxWidth: '500px', position: 'relative' }}>
              <span className="input-icon-field" style={{ position: 'absolute', right: isRTL ? '12px' : 'auto', left: isRTL ? 'auto' : '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>📍</span>
              <input 
                className="input-styled-icon" 
                style={{ width: '100%', paddingRight: isRTL ? '45px' : '12px', paddingLeft: isRTL ? '12px' : '45px', fontSize: isRTL ? '1.1rem' : '0.9rem', direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                placeholder={t('placeholder_route_example', "Ex: Ghandi, Maarif...")} 
                value={formData.routeDescription || ''} 
                onChange={e => setFormData({...formData, routeDescription: e.target.value})} 
              />
            </div>
          </div>

          <div className="button-group-footer" style={{ marginTop: '30px', display: 'flex', gap: '20px', justifyContent: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <button 
              type="button" 
              onClick={() => { setViewMode('list'); setEditingId(null); }} 
              style={{ padding: '16px 35px', borderRadius: '30px', background: '#e2e8f0', color: '#4a5568', fontWeight: 800, border: 'none', cursor: 'pointer', minWidth: '160px' }}
            >
              {t('btn_cancel') || "ANNULER"}
            </button>
            <button 
              className="btn-valider-inscription" 
              onClick={handleSave} 
              style={{ padding: '16px 50px', borderRadius: '30px', fontWeight: 800, width: 'auto', minWidth: '220px' }}
            >
              {editingId ? t('btn_save').toUpperCase() : t('btn_add_bus').toUpperCase()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusManagement;